import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { progressAPI, goalsAPI } from '../../api';
import { Spinner } from '../../components/ui';
import {
  Scale, TrendingUp, TrendingDown, Plus, Activity, X, LineChart,
  Camera, Trash2, Sparkles, Calendar, Target, Zap, ImageIcon,
  Award, ChevronDown, ChevronUp, Ruler
} from 'lucide-react';

/**
 * ProgressTracking — /progress
 *
 * Body composition + measurement visualisations. Performance Club
 * dark theme throughout: dark surface, cream text, lime primary,
 * translucent borders, hero-kicker / editorial-panel vocabulary.
 *
 * Layout:
 *   1. Hero row: period selector + "Record metric" CTA
 *   2. 4 summary cards (weight change, body fat change, sessions, days)
 *      with delta arrows and SVP-style "coach" insight line per card
 *   3. Two SVG line charts side by side (weight, body fat) with
 *      hover tooltips, min/max markers, and trend direction
 *   4. Latest measurements grid (10 metrics) with delta + click-to-chart
 *   5. Photo timeline: side-by-side latest vs oldest (or single if
 *      only one), with diff annotation
 *   6. Recent PRs (uses backend's PR data from summary)
 *
 * Record-metric modal in a fixed overlay, Performance Club styled.
 */

const METRIC_TYPES = [
  { value: 'weight', label: 'Weight', unit: 'kg', icon: Scale, color: 'primary' },
  { value: 'body_fat', label: 'Body Fat', unit: '%', icon: Activity, color: 'blue' },
  { value: 'muscle mass', label: 'Muscle Mass', unit: 'kg', icon: Zap, color: 'amber' },
  { value: 'bmi', label: 'BMI', unit: '', icon: Target, color: 'purple' },
  { value: 'chest', label: 'Chest', unit: 'cm', icon: Ruler, color: 'primary' },
  { value: 'waist', label: 'Waist', unit: 'cm', icon: Ruler, color: 'amber' },
  { value: 'hips', label: 'Hips', unit: 'cm', icon: Ruler, color: 'purple' },
  { value: 'biceps_left', label: 'Biceps L', unit: 'cm', icon: Ruler, color: 'blue' },
  { value: 'biceps_right', label: 'Biceps R', unit: 'cm', icon: Ruler, color: 'blue' },
  { value: 'thigh_left', label: 'Thigh L', unit: 'cm', icon: Ruler, color: 'amber' },
];

export function ProgressTracking() {
  const { t } = useTranslation();
  const [summary, setSummary] = useState(null);
  const [latestMetrics, setLatestMetrics] = useState({});
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showPhotoForm, setShowPhotoForm] = useState(false);
  const [selectedMetric, setSelectedMetric] = useState(null);
  const [timeRange, setTimeRange] = useState(90);
  const [selectedPhotos, setSelectedPhotos] = useState({ before: null, after: null });
  const [goals, setGoals] = useState([]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [summaryRes, latestRes, goalsRes] = await Promise.all([
        progressAPI.getSummary(timeRange),
        progressAPI.getLatestMetrics(),
        goalsAPI.list({ active_only: true }).catch(() => ({ data: [] })),
      ]);
      setSummary(summaryRes.data);
      setLatestMetrics(latestRes.data);
      setGoals(goalsRes.data || []);
    } catch (error) {
      console.error('Error fetching progress data:', error);
    } finally {
      setLoading(false);
    }
  }, [timeRange]);

  // Find the most recent active goal for a given metric (lowest created_at wins for ties)
  const targetForMetric = useCallback(
    (metric) => {
      const matches = goals.filter(
        (g) => g.metric === metric && !g.is_completed && g.target_value != null
      );
      if (matches.length === 0) return null;
      // Prefer the goal with the closest deadline (most urgent), fall back to first created
      return matches.reduce((best, g) => {
        if (!best) return g;
        if (g.deadline && !best.deadline) return g;
        if (g.deadline && best.deadline) {
          return new Date(g.deadline) < new Date(best.deadline) ? g : best;
        }
        return best;
      }, null);
    },
    [goals]
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Pick the auto before/after photos once data lands
  useEffect(() => {
    if (!summary?.photos?.length) return;
    const sorted = [...summary.photos].sort(
      (a, b) => new Date(a.taken_at) - new Date(b.taken_at)
    );
    if (sorted.length === 1) {
      setSelectedPhotos({ before: sorted[0], after: sorted[0] });
    } else {
      setSelectedPhotos({ before: sorted[0], after: sorted[sorted.length - 1] });
    }
  }, [summary]);

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm(t('progress.confirmDeletePhoto', { defaultValue: 'Delete this photo?' }))) return;
    try {
      await progressAPI.deletePhoto(photoId);
      await fetchData();
    } catch (error) {
      console.error('Failed to delete photo:', error);
    }
  };

  if (loading && !summary) {
    return (
      <div className="flex items-center justify-center min-h-[420px]">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!summary) return null;

  return (
    <div className="space-y-6" data-testid="progress-tracking">
      <PageHeader
        timeRange={timeRange}
        setTimeRange={setTimeRange}
        onRecord={() => setShowForm(true)}
        onUploadPhoto={() => setShowPhotoForm(true)}
        t={t}
      />

      <SummaryRow summary={summary} t={t} />

      <ChartsRow
        summary={summary}
        targetWeight={targetForMetric('weight')}
        targetBodyFat={targetForMetric('body_fat')}
        t={t}
      />

      <LatestMetricsGrid
        latestMetrics={latestMetrics}
        measurements={summary.measurements}
        onSelect={setSelectedMetric}
        t={t}
      />

      {selectedMetric && (
        <MetricHistoryPanel
          metric={selectedMetric}
          measurements={summary.measurements}
          onClose={() => setSelectedMetric(null)}
          t={t}
        />
      )}

      <PhotoTimeline
        photos={summary.photos || []}
        selected={selectedPhotos}
        setSelected={setSelectedPhotos}
        onDelete={handleDeletePhoto}
        t={t}
      />

      {summary.prs && summary.prs.length > 0 && (
        <RecentPRs prs={summary.prs} t={t} />
      )}

      {showForm && (
        <MetricForm
          onClose={() => setShowForm(false)}
          onSave={async () => { setShowForm(false); await fetchData(); }}
          t={t}
        />
      )}

      {showPhotoForm && (
        <PhotoForm
          onClose={() => setShowPhotoForm(false)}
          onSave={async () => { setShowPhotoForm(false); await fetchData(); }}
          t={t}
        />
      )}
    </div>
  );
}

// ===========================================================================
// Page chrome
// ===========================================================================

function PageHeader({ timeRange, setTimeRange, onRecord, onUploadPhoto, t }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
      <div>
        <span className="hero-kicker">
          {t('progress.kicker', { defaultValue: 'Body composition' })}
        </span>
        <h1 className="mt-2 font-display text-3xl uppercase text-[var(--color-text)]">
          {t('progress.title', { defaultValue: 'Your progress' })}
        </h1>
        <p className="mt-1 text-sm text-[var(--color-text-muted)] max-w-2xl">
          {t('progress.subtitle', {
            defaultValue: 'Track weight, body fat, and measurements over time. The trend tells the story.',
          })}
        </p>
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        <PeriodSelector value={timeRange} onChange={setTimeRange} t={t} />
        <button
          type="button"
          onClick={onUploadPhoto}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-2xl border border-[var(--color-border)] bg-white/[0.04] text-[var(--color-text)] text-sm font-medium hover:bg-white/[0.08] transition-colors"
          data-testid="upload-photo-btn"
        >
          <Camera className="w-4 h-4" />
          {t('progress.ctaPhoto', { defaultValue: 'Photo' })}
        </button>
        <button
          type="button"
          onClick={onRecord}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-2xl bg-gradient-to-r from-primary-500 to-primary-300 text-[#0a1208] text-sm font-semibold hover:from-primary-300 hover:to-primary-200 transition-all"
          data-testid="record-metric-btn"
        >
          <Plus className="w-4 h-4" />
          {t('progress.ctaRecord', { defaultValue: 'Record metric' })}
        </button>
      </div>
    </div>
  );
}

function PeriodSelector({ value, onChange, t }) {
  const options = [
    { v: 30, label: '30 d' },
    { v: 90, label: '90 d' },
    { v: 180, label: '6 m' },
    { v: 365, label: '1 y' },
  ];
  return (
    <div
      className="inline-flex p-1 rounded-2xl border border-[var(--color-border)] bg-white/[0.04]"
      role="tablist"
    >
      {options.map((o) => {
        const active = value === o.v;
        return (
          <button
            key={o.v}
            type="button"
            onClick={() => onChange(o.v)}
            className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-colors ${
              active
                ? 'bg-primary-300 text-[#0a1208]'
                : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)]'
            }`}
            data-testid={`progress-period-${o.v}`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

// ===========================================================================
// Summary cards
// ===========================================================================

function SummaryRow({ summary, t }) {
  const cards = [
    {
      label: t('progress.weightChange', { defaultValue: 'Weight change' }),
      value: formatDelta(summary.total_weight_change),
      unit: summary.total_weight_change != null ? 'kg' : '—',
      insight: coachForWeight(summary.total_weight_change, t),
      icon: Scale,
      color: 'primary',
    },
    {
      label: t('progress.bodyFatChange', { defaultValue: 'Body fat change' }),
      value: formatDelta(summary.total_body_fat_change),
      unit: summary.total_body_fat_change != null ? '%' : '—',
      insight: coachForBodyFat(summary.total_body_fat_change, t),
      icon: Activity,
      color: 'blue',
    },
    {
      label: t('progress.sessionsCompleted', { defaultValue: 'Sessions completed' }),
      value: summary.sessions_completed,
      unit: t('progress.inPeriod', { defaultValue: 'in period' }),
      insight: sessionsInsight(summary.sessions_completed, t),
      icon: Award,
      color: 'purple',
    },
    {
      label: t('progress.daysActive', { defaultValue: 'Active days' }),
      value: summary.days_active,
      unit: t('progress.distinctDays', { defaultValue: 'distinct days' }),
      insight: daysInsight(summary.days_active, t),
      icon: Calendar,
      color: 'amber',
    },
  ];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="progress-summary">
      {cards.map((c) => (
        <SummaryCard key={c.label} {...c} />
      ))}
    </div>
  );
}

function SummaryCard({ label, value, unit, insight, icon: Icon, color = 'primary' }) {
  const colorClasses = {
    primary: 'border-[rgba(213,255,123,0.18)] bg-[rgba(213,255,123,0.05)] text-primary-100',
    blue: 'border-[rgba(95,180,255,0.18)] bg-[rgba(95,180,255,0.05)] text-blue-200',
    purple: 'border-[rgba(195,140,255,0.18)] bg-[rgba(195,140,255,0.05)] text-purple-200',
    amber: 'border-[rgba(255,210,125,0.18)] bg-[rgba(255,210,125,0.05)] text-amber-200',
  };
  return (
    <div
      className="rounded-2xl border border-[var(--color-border)] bg-[rgba(8,13,11,0.5)] p-4"
      data-testid={`summary-card-${color}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className={cn('p-2 rounded-xl border shrink-0', colorClasses[color])}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="text-right">
          <div className="font-display text-2xl text-[var(--color-text)] leading-none">
            {value}
          </div>
          <div className="mt-1 text-[10px] uppercase tracking-[0.18em] text-[var(--color-text-muted)]">
            {unit}
          </div>
        </div>
      </div>
      <div className="mt-3 text-xs text-[var(--color-text-muted)]">{label}</div>
      {insight && (
        <div className="mt-2 text-[10px] leading-tight text-[var(--color-text-muted)] flex items-start gap-1">
          <Sparkles className="w-3 h-3 mt-0.5 text-primary-200 shrink-0" />
          <span>{insight}</span>
        </div>
      )}
    </div>
  );
}

function coachForWeight(delta, t) {
  if (delta == null) return t('progress.coachLogFirst', { defaultValue: 'Log weight to start tracking' });
  if (delta < -2) return t('progress.coachCutting', { defaultValue: 'Cutting phase. Protein up, sleep up.' });
  if (delta < -0.5) return t('progress.coachSlowCut', { defaultValue: 'Slow cut. Sustainable rate.' });
  if (delta > 2) return t('progress.coachBulking', { defaultValue: 'Surplus. Push intensity.' });
  if (delta > 0.5) return t('progress.coachSlowBulk', { defaultValue: 'Lean bulk territory.' });
  return t('progress.coachMaintain', { defaultValue: 'Maintenance. Hit your training hard.' });
}

function coachForBodyFat(delta, t) {
  if (delta == null) return t('progress.coachLogBfFirst', { defaultValue: 'Log body fat to start tracking' });
  if (delta < -2) return t('progress.coachBfDown', { defaultValue: 'Strong recomposition. Keep going.' });
  if (delta < -0.5) return t('progress.coachBfSlowDown', { defaultValue: 'Steady fat loss.' });
  if (delta > 2) return t('progress.coachBfUp', { defaultValue: 'Watch calories. Lift heavy.' });
  if (delta > 0.5) return t('progress.coachBfCreep', { defaultValue: 'Slight up. Stay accountable.' });
  return t('progress.coachBfStable', { defaultValue: 'Stable. Focus on the barbell.' });
}

function sessionsInsight(n, t) {
  if (n === 0) return t('progress.coachNoSessions', { defaultValue: 'Log a session to start the streak.' });
  if (n < 8) return t('progress.coachFewSessions', { defaultValue: 'Building the habit. Show up again.' });
  if (n < 24) return t('progress.coachSteadySessions', { defaultValue: 'Solid cadence. Keep stacking.' });
  return t('progress.coachManySessions', { defaultValue: 'High volume. Watch recovery.' });
}

function daysInsight(n, t) {
  if (n === 0) return t('progress.coachNoDays', { defaultValue: 'No active days yet.' });
  if (n < 8) return t('progress.coachFewDays', { defaultValue: 'Sporadic. Schedule the days.' });
  if (n < 24) return t('progress.coachSteadyDays', { defaultValue: 'Coming in consistently.' });
  return t('progress.coachManyDays', { defaultValue: 'Elite consistency.' });
}

function formatDelta(d) {
  if (d == null) return '—';
  if (d === 0) return '0';
  return d > 0 ? `+${d.toFixed(1)}` : d.toFixed(1);
}

// ===========================================================================
// Charts row (SVG line charts)
// ===========================================================================

function ChartsRow({ summary, targetWeight, targetBodyFat, t }) {
  const hasWeight = summary.weight_history?.length > 0;
  const hasBf = summary.body_fat_history?.length > 0;
  if (!hasWeight && !hasBf) {
    return <EmptyChartsState t={t} />;
  }
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="progress-charts">
      {hasWeight ? (
        <LineChartPanel
          title={t('progress.weightHistory', { defaultValue: 'Weight' })}
          unit="kg"
          data={summary.weight_history}
          t={t}
          lowerIsBetter
          testId="weight-chart"
          target={targetWeight}
        />
      ) : <EmptyChartPlaceholder title={t('progress.weightHistory', { defaultValue: 'Weight' })} t={t} />}
      {hasBf ? (
        <LineChartPanel
          title={t('progress.bodyFatHistory', { defaultValue: 'Body fat' })}
          unit="%"
          data={summary.body_fat_history}
          t={t}
          lowerIsBetter
          testId="body-fat-chart"
          target={targetBodyFat}
        />
      ) : <EmptyChartPlaceholder title={t('progress.bodyFatHistory', { defaultValue: 'Body fat' })} t={t} />}
    </div>
  );
}

function EmptyChartsState({ t }) {
  return (
    <div className="rounded-2xl border border-dashed border-[var(--color-border)] bg-[rgba(8,13,11,0.4)] p-12 text-center">
      <LineChart className="w-8 h-8 text-[var(--color-text-muted)] mx-auto mb-3" />
      <p className="font-display text-lg text-[var(--color-text)]">
        {t('progress.charts.emptyTitle', { defaultValue: 'No data to plot yet' })}
      </p>
      <p className="mt-1 text-sm text-[var(--color-text-muted)] max-w-md mx-auto">
        {t('progress.charts.emptyHint', {
          defaultValue: 'Record your first weight or body fat measurement to see your trend line.',
        })}
      </p>
    </div>
  );
}

function EmptyChartPlaceholder({ title, t }) {
  return (
    <div className="rounded-2xl border border-dashed border-[var(--color-border)] bg-[rgba(8,13,11,0.3)] p-8 text-center">
      <p className="text-sm text-[var(--color-text-muted)]">{title}</p>
      <p className="mt-1 text-[10px] uppercase tracking-[0.18em] text-[var(--color-text-muted)]">
        {t('progress.charts.placeholder', { defaultValue: 'No data in period' })}
      </p>
    </div>
  );
}

function LineChartPanel({ title, unit, data, t, lowerIsBetter, testId, target }) {
  const [hoveredIdx, setHoveredIdx] = useState(null);
  const wrapRef = useRef(null);

  if (!data?.length) return null;

  // Normalise data
  const points = data.map((d) => ({
    date: new Date(d.date),
    value: d.value,
  }));
  const values = points.map((p) => p.value);
  // If a target exists, expand the visible Y range so the line lands inside the chart
  // (with at least 8% padding above/below the data+target band).
  const targetValue = target?.target_value ?? null;
  const allValues = targetValue != null ? [...values, targetValue] : values;
  const rawMin = Math.min(...allValues);
  const rawMax = Math.max(...allValues);
  const rawRange = rawMax - rawMin || 1;
  const minVal = rawMin - rawRange * 0.08;
  const maxVal = rawMax + rawRange * 0.08;
  const startVal = points[0].value;
  const endVal = points[points.length - 1].value;
  const delta = endVal - startVal;
  const range = maxVal - minVal;
  // SVG dimensions
  const W = 600;
  const H = 200;
  const padL = 50;
  const padR = 24;
  const padT = 24;
  const padB = 32;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;

  const xFor = (i) => padL + (i / Math.max(points.length - 1, 1)) * innerW;
  const yFor = (v) => padT + innerH - ((v - minVal) / range) * innerH;

  const pathD = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${xFor(i).toFixed(1)} ${yFor(p.value).toFixed(1)}`)
    .join(' ');

  const areaD = `${pathD} L ${xFor(points.length - 1).toFixed(1)} ${(padT + innerH).toFixed(1)} L ${xFor(0).toFixed(1)} ${(padT + innerH).toFixed(1)} Z`;

  const isUp = delta > 0;
  const isDown = delta < 0;
  // For weight + body fat, down is good; for muscle mass up is good
  const trendGood = lowerIsBetter ? isDown : isUp;
  const trendColor = delta === 0
    ? 'text-[var(--color-text-muted)]'
    : (lowerIsBetter ? isDown : isUp)
    ? 'text-primary-100'
    : 'text-amber-200';

  return (
    <div className="rounded-2xl border border-[var(--color-border)] bg-[rgba(8,13,11,0.5)] p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <span className="hero-kicker">{t('progress.chart.kicker', { defaultValue: 'Trend' })}</span>
          <h3 className="mt-1 font-display text-lg uppercase text-[var(--color-text)]">
            {title}
          </h3>
        </div>
        <div className="text-right">
          <div className="font-display text-2xl text-[var(--color-text)]">
            {endVal.toFixed(1)}<span className="text-sm text-[var(--color-text-muted)] ml-1">{unit}</span>
          </div>
          <div className={`text-xs ${trendColor} flex items-center justify-end gap-1`}>
            {delta > 0 ? <TrendingUp className="w-3 h-3" /> : delta < 0 ? <TrendingDown className="w-3 h-3" /> : null}
            {formatDelta(delta)} {unit} {t('progress.chart.inPeriod', { defaultValue: 'in period' })}
          </div>
          {target && (
            <div
              className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-amber-300/30 bg-amber-300/10 text-[10px] uppercase tracking-[0.14em] text-amber-200"
              data-testid={`${testId}-target-badge`}
              title={target.notes || undefined}
            >
              <Target className="w-2.5 h-2.5" />
              {t('progress.chart.target', { defaultValue: 'Target' })} {target.target_value.toFixed(1)}{unit}
            </div>
          )}
        </div>
      </div>

      <div ref={wrapRef} className="relative" data-testid={testId}>
        <svg viewBox={`0 0 ${W} ${H}`} xmlns="http://www.w3.org/2000/svg" className="w-full h-auto">
          <defs>
            <linearGradient id={`${testId}-gradient`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="rgba(213, 255, 123, 0.35)" />
              <stop offset="100%" stopColor="rgba(213, 255, 123, 0)" />
            </linearGradient>
          </defs>

          {/* Y-axis grid lines */}
          {[0, 0.25, 0.5, 0.75, 1].map((p) => (
            <line
              key={p}
              x1={padL}
              y1={padT + innerH * p}
              x2={W - padR}
              y2={padT + innerH * p}
              stroke="rgba(255, 255, 255, 0.04)"
              strokeWidth="1"
            />
          ))}

          {/* Y-axis labels (min / mid / max) */}
          {[maxVal, (maxVal + minVal) / 2, minVal].map((v, i) => (
            <text
              key={i}
              x={padL - 8}
              y={padT + (i * innerH / 2) + 4}
              textAnchor="end"
              className="fill-[var(--color-text-muted)]"
              style={{ fontSize: 10 }}
            >
              {v.toFixed(1)}
            </text>
          ))}

          {/* X-axis labels (first / mid / last date) */}
          {[0, Math.floor((points.length - 1) / 2), points.length - 1].map((i, idx) => (
            <text
              key={idx}
              x={xFor(i)}
              y={H - padB + 18}
              textAnchor={idx === 0 ? 'start' : idx === 2 ? 'end' : 'middle'}
              className="fill-[var(--color-text-muted)]"
              style={{ fontSize: 10 }}
            >
              {formatShortDate(points[i].date)}
            </text>
          ))}

          {/* Area fill */}
          <path d={areaD} fill={`url(#${testId}-gradient)`} />

          {/* Line */}
          <path
            d={pathD}
            fill="none"
            stroke="rgba(213, 255, 123, 0.95)"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />

          {/* Target line — dashed amber horizontal at the user's goal value */}
          {targetValue != null && (
            <g data-testid={`${testId}-target`}>
              <line
                x1={padL}
                y1={yFor(targetValue)}
                x2={W - padR}
                y2={yFor(targetValue)}
                stroke="rgba(255, 199, 95, 0.85)"
                strokeWidth="1.5"
                strokeDasharray="6 4"
                strokeLinecap="round"
              />
              {/* Filled bullet on the right edge */}
              <circle
                cx={W - padR}
                cy={yFor(targetValue)}
                r="4"
                fill="rgba(255, 199, 95, 1)"
                stroke="#0a1208"
                strokeWidth="1.5"
              />
              {/* Y-axis label for the target value */}
              <text
                x={padL - 8}
                y={yFor(targetValue) + 4}
                textAnchor="end"
                className="fill-amber-200"
                style={{ fontSize: 10, fontWeight: 600 }}
                data-testid={`${testId}-target-label`}
              >
                {targetValue.toFixed(1)} ▸
              </text>
            </g>
          )}

          {/* Data points */}
          {points.map((p, i) => (
            <g key={i}>
              <circle
                cx={xFor(i)}
                cy={yFor(p.value)}
                r={hoveredIdx === i ? 5 : 3}
                fill={hoveredIdx === i ? 'rgba(213, 255, 123, 1)' : 'rgba(213, 255, 123, 0.8)'}
                stroke="#0a1208"
                strokeWidth="1.5"
                style={{ transition: 'r 0.15s ease' }}
              />
              {/* Hover hitbox */}
              <rect
                x={xFor(i) - (innerW / points.length) / 2}
                y={padT}
                width={innerW / points.length}
                height={innerH}
                fill="transparent"
                onMouseEnter={() => setHoveredIdx(i)}
                onMouseLeave={() => setHoveredIdx(null)}
                style={{ cursor: 'pointer' }}
                data-testid={`${testId}-point-${i}`}
              />
            </g>
          ))}
        </svg>

        {/* Hover tooltip */}
        {hoveredIdx !== null && (
          <div
            className="absolute pointer-events-none rounded-xl border border-[var(--color-border)] bg-[#0a1208]/95 backdrop-blur-sm px-3 py-2 shadow-2xl"
            style={{
              left: `${(xFor(hoveredIdx) / W) * 100}%`,
              top: `${(yFor(points[hoveredIdx].value) / H) * 100}%`,
              transform: 'translate(-50%, -130%)',
            }}
            data-testid={`${testId}-tooltip`}
          >
            <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--color-text-muted)]">
              {formatFullDate(points[hoveredIdx].date)}
            </div>
            <div className="font-display text-lg text-[var(--color-text)]">
              {points[hoveredIdx].value.toFixed(1)}<span className="text-xs text-[var(--color-text-muted)] ml-1">{unit}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ===========================================================================
// Latest measurements grid
// ===========================================================================

function LatestMetricsGrid({ latestMetrics, measurements, onSelect, t }) {
  return (
    <div
      className="rounded-2xl border border-[var(--color-border)] bg-[rgba(8,13,11,0.5)] p-6"
      data-testid="latest-metrics"
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <span className="hero-kicker">{t('progress.latest.kicker', { defaultValue: 'Latest measurements' })}</span>
          <h3 className="mt-1 font-display text-lg uppercase text-[var(--color-text)]">
            {t('progress.latest.title', { defaultValue: 'All metrics' })}
          </h3>
        </div>
        <Ruler className="w-5 h-5 text-primary-200" />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {METRIC_TYPES.map((metric) => {
          const latest = latestMetrics[metric.value];
          const meas = measurements?.[metric.value];
          const value = latest?.value ?? meas?.current_value;
          const change = meas?.total_change;
          const date = latest?.measured_at;
          return (
            <button
              key={metric.value}
              type="button"
              onClick={() => meas && onSelect(metric.value)}
              disabled={!meas}
              className="rounded-2xl border border-[var(--color-border)] bg-white/[0.02] p-3 text-left hover:bg-white/[0.05] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid={`metric-tile-${metric.value}`}
            >
              <div className="flex items-center gap-2 mb-2">
                <div
                  className={cn(
                    'p-1.5 rounded-lg border',
                    colorClasses(metric.color)
                  )}
                >
                  <metric.icon className="w-3.5 h-3.5" />
                </div>
                <span className="text-xs uppercase tracking-[0.12em] text-[var(--color-text-muted)] truncate">
                  {metric.label}
                </span>
              </div>
              <div className="font-display text-xl text-[var(--color-text)]">
                {value != null ? value.toFixed(1) : '—'}
                {value != null && <span className="text-xs text-[var(--color-text-muted)] ml-1">{latest?.unit || metric.unit}</span>}
              </div>
              {change != null && Math.abs(change) >= 0.05 && (
                <div className={`mt-1 text-[10px] flex items-center gap-0.5 ${change > 0 ? 'text-primary-100' : 'text-amber-200'}`}>
                  {change > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                  {formatDelta(change)} {latest?.unit || metric.unit}
                </div>
              )}
              {date && (
                <div className="mt-1 text-[10px] text-[var(--color-text-muted)]">
                  {formatShortDate(new Date(date))}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function MetricHistoryPanel({ metric, measurements, onClose, t }) {
  const meas = measurements?.[metric];
  if (!meas) return null;
  const label = t(`progress.metric.${metric}`, { defaultValue: metric });
  return (
    <div
      className="rounded-2xl border border-[var(--color-border)] bg-[rgba(8,13,11,0.5)] p-6"
      data-testid="metric-history-panel"
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <span className="hero-kicker">{t('progress.history.kicker', { defaultValue: 'History' })}</span>
          <h3 className="mt-1 font-display text-lg uppercase text-[var(--color-text)]">
            {label}
          </h3>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-xs uppercase tracking-[0.18em] text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
        >
          ✕
        </button>
      </div>
      <LineChartPanel
        title={label}
        unit={meas.unit || ''}
        data={meas.entries}
        t={t}
        lowerIsBetter={metric !== 'muscle mass'}
        testId={`metric-${metric}-chart`}
      />
    </div>
  );
}

// ===========================================================================
// Photo timeline
// ===========================================================================

function PhotoTimeline({ photos, selected, setSelected, onDelete, t }) {
  const [collapsed, setCollapsed] = useState(false);
  if (!photos || photos.length === 0) {
    return <EmptyPhotoTimeline t={t} onUploadPhoto={() => {}} />;
  }
  return (
    <div
      className="rounded-2xl border border-[var(--color-border)] bg-[rgba(8,13,11,0.5)] p-6"
      data-testid="photo-timeline"
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <span className="hero-kicker">{t('progress.photo.kicker', { defaultValue: 'Visual timeline' })}</span>
          <h3 className="mt-1 font-display text-lg uppercase text-[var(--color-text)]">
            {t('progress.photo.title', { defaultValue: 'Photo progress' })}
          </h3>
        </div>
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          className="text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
          data-testid="photo-timeline-toggle"
        >
          {collapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
        </button>
      </div>

      {!collapsed && (
        <>
          {/* Side-by-side comparison */}
          {selected.before && selected.after && selected.before.id !== selected.after.id && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4" data-testid="photo-compare">
              <PhotoCard photo={selected.before} label={t('progress.photo.before', { defaultValue: 'Before' })} t={t} onDelete={onDelete} muted />
              <PhotoCard photo={selected.after} label={t('progress.photo.after', { defaultValue: 'After' })} t={t} onDelete={onDelete} />
            </div>
          )}
          {selected.before && selected.after && selected.before.id === selected.after.id && (
            <PhotoCard photo={selected.before} label={t('progress.photo.only', { defaultValue: 'Your only photo' })} t={t} onDelete={onDelete} />
          )}

          {/* Strip selector */}
          {photos.length > 2 && (
            <div className="mt-3">
              <p className="text-xs uppercase tracking-[0.18em] text-[var(--color-text-muted)] mb-2">
                {t('progress.photo.choosePair', { defaultValue: 'Pick before / after' })}
              </p>
              <div className="flex gap-2 overflow-x-auto pb-2">
                {[...photos]
                  .sort((a, b) => new Date(a.taken_at) - new Date(b.taken_at))
                  .map((p) => {
                    const isBefore = p.id === selected.before?.id;
                    const isAfter = p.id === selected.after?.id;
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => {
                          // Click on a photo toggles before/after. Click on before = set as before, click on after = set as after.
                          if (isBefore) {
                            setSelected((s) => ({ ...s, before: null }));
                          } else if (isAfter) {
                            setSelected((s) => ({ ...s, after: null }));
                          } else if (!isBefore && !selected.before) {
                            setSelected((s) => ({ ...s, before: p }));
                          } else {
                            setSelected((s) => ({ ...s, after: p }));
                          }
                        }}
                        className={cn(
                          'shrink-0 relative rounded-xl border-2 overflow-hidden transition-all',
                          (isBefore || isAfter) ? 'border-primary-300' : 'border-[var(--color-border)] opacity-70 hover:opacity-100'
                        )}
                        style={{ width: 80, height: 100 }}
                        data-testid={`photo-thumb-${p.id}`}
                      >
                        <img
                          src={p.photo_url}
                          alt={p.notes || 'progress'}
                          className="w-full h-full object-cover"
                        />
                        {isBefore && (
                          <div className="absolute top-1 left-1 px-1.5 py-0.5 rounded-md bg-[#0a1208]/80 text-[10px] uppercase tracking-[0.12em] text-primary-100">
                            {t('progress.photo.before', { defaultValue: 'Before' })}
                          </div>
                        )}
                        {isAfter && (
                          <div className="absolute top-1 right-1 px-1.5 py-0.5 rounded-md bg-[#0a1208]/80 text-[10px] uppercase tracking-[0.12em] text-primary-100">
                            {t('progress.photo.after', { defaultValue: 'After' })}
                          </div>
                        )}
                        <div className="absolute bottom-0 left-0 right-0 px-1 py-0.5 bg-[#0a1208]/80 text-[9px] text-[var(--color-text-muted)] text-center">
                          {formatShortDate(new Date(p.taken_at))}
                        </div>
                      </button>
                    );
                  })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function PhotoCard({ photo, label, t, onDelete, muted }) {
  return (
    <div className={`rounded-2xl border ${muted ? 'border-[var(--color-border)]' : 'border-primary-200/30'} overflow-hidden bg-white/[0.02] relative`}>
      <div className="px-3 py-1.5 border-b border-[var(--color-border)] flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-[0.18em] text-[var(--color-text-muted)]">{label}</span>
        {onDelete && (
          <button
            type="button"
            onClick={() => onDelete(photo.id)}
            className="text-[var(--color-text-muted)] hover:text-red-200"
            data-testid={`photo-delete-${photo.id}`}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      <div className="aspect-[3/4] bg-[#0a1208] flex items-center justify-center overflow-hidden">
        {photo.photo_url ? (
          <img
            src={photo.photo_url}
            alt={photo.notes || 'progress'}
            className="w-full h-full object-cover"
            data-testid={`photo-img-${photo.id}`}
          />
        ) : (
          <ImageIcon className="w-8 h-8 text-[var(--color-text-muted)]" />
        )}
      </div>
      <div className="p-3 text-xs text-[var(--color-text-muted)]">
        {formatFullDate(new Date(photo.taken_at))}
        {photo.notes && <div className="mt-1 text-[var(--color-text)]">{photo.notes}</div>}
      </div>
    </div>
  );
}

function EmptyPhotoTimeline({ t, onUploadPhoto }) {
  return (
    <div className="rounded-2xl border border-dashed border-[var(--color-border)] bg-[rgba(8,13,11,0.4)] p-8 text-center">
      <Camera className="w-8 h-8 text-[var(--color-text-muted)] mx-auto mb-3" />
      <p className="font-display text-lg text-[var(--color-text)]">
        {t('progress.photo.emptyTitle', { defaultValue: 'No photos yet' })}
      </p>
      <p className="mt-1 text-sm text-[var(--color-text-muted)] max-w-md mx-auto">
        {t('progress.photo.emptyHint', {
          defaultValue: 'Upload a photo and another one later to see your visual progress side by side.',
        })}
      </p>
    </div>
  );
}

// ===========================================================================
// Recent PRs
// ===========================================================================

function RecentPRs({ prs, t }) {
  return (
    <div
      className="rounded-2xl border border-[var(--color-border)] bg-[rgba(8,13,11,0.5)] p-6"
      data-testid="recent-prs"
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <span className="hero-kicker">{t('progress.prs.kicker', { defaultValue: 'Strength' })}</span>
          <h3 className="mt-1 font-display text-lg uppercase text-[var(--color-text)]">
            {t('progress.prs.title', { defaultValue: 'Recent personal records' })}
          </h3>
        </div>
        <Award className="w-5 h-5 text-primary-200" />
      </div>
      <ul className="space-y-2">
        {prs.slice(0, 6).map((pr) => (
          <li
            key={pr.id}
            className="flex items-center gap-3 p-3 rounded-2xl border border-[var(--color-border)] bg-white/[0.02]"
            data-testid={`pr-row-${pr.id}`}
          >
            <div className="shrink-0 h-8 w-8 rounded-full bg-gradient-to-br from-primary-500 to-primary-300 text-[#0a1208] flex items-center justify-center text-sm font-display">
              <Award className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm text-[var(--color-text)] font-medium truncate">
                {pr.exercise_name}
              </div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--color-text-muted)]">
                {formatShortDate(new Date(pr.achieved_at))}
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className="font-display text-sm text-primary-100">
                {pr.value} <span className="text-xs text-[var(--color-text-muted)]">{pr.unit}</span>
              </div>
              {pr.improvement != null && (
                <div className="text-[10px] text-primary-100">+{pr.improvement.toFixed(1)}%</div>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ===========================================================================
// Forms
// ===========================================================================

function MetricForm({ onClose, onSave, t }) {
  const [formData, setFormData] = useState({
    metric_type: 'weight',
    value: '',
    unit: 'kg',
    notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const selectedMetric = METRIC_TYPES.find((m) => m.value === formData.metric_type);
  useEffect(() => {
    if (selectedMetric) {
      setFormData((prev) => ({ ...prev, unit: selectedMetric.unit }));
    }
  }, [formData.metric_type]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.value) {
      setError(t('progress.form.errorValue', { defaultValue: 'Please enter a value' }));
      return;
    }
    setSaving(true);
    setError('');
    try {
      await progressAPI.recordMetric({
        ...formData,
        value: parseFloat(formData.value),
      });
      onSave();
    } catch (err) {
      setError(err.response?.data?.detail || t('progress.form.errorSave', { defaultValue: 'Failed to save metric' }));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="metric-form-modal">
      <div className="rounded-2xl border border-[var(--color-border)] bg-[#0d1512] w-full max-w-md overflow-hidden">
        <div className="px-6 py-4 border-b border-[var(--color-border)] flex items-center justify-between">
          <div>
            <span className="hero-kicker">{t('progress.form.kicker', { defaultValue: 'New entry' })}</span>
            <h2 className="mt-1 font-display text-lg uppercase text-[var(--color-text)]">
              {t('progress.form.title', { defaultValue: 'Record a metric' })}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
            data-testid="metric-form-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-xs uppercase tracking-[0.18em] text-[var(--color-text-muted)] mb-1.5">
              {t('progress.form.metric', { defaultValue: 'Metric' })}
            </label>
            <select
              value={formData.metric_type}
              onChange={(e) => setFormData((p) => ({ ...p, metric_type: e.target.value }))}
              className="w-full px-3 py-2 rounded-xl border border-[var(--color-border)] bg-white/[0.04] text-[var(--color-text)] text-sm focus:outline-none focus:border-primary-200/40"
              data-testid="metric-form-type"
            >
              {METRIC_TYPES.map((m) => (
                <option key={m.value} value={m.value} className="bg-[#0a1208]">
                  {m.label}
                </option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs uppercase tracking-[0.18em] text-[var(--color-text-muted)] mb-1.5">
                {t('progress.form.value', { defaultValue: 'Value' })}
              </label>
              <input
                type="number"
                step="0.1"
                value={formData.value}
                onChange={(e) => setFormData((p) => ({ ...p, value: e.target.value }))}
                className="w-full px-3 py-2 rounded-xl border border-[var(--color-border)] bg-white/[0.04] text-[var(--color-text)] text-sm focus:outline-none focus:border-primary-200/40"
                data-testid="metric-form-value"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.18em] text-[var(--color-text-muted)] mb-1.5">
                {t('progress.form.unit', { defaultValue: 'Unit' })}
              </label>
              <select
                value={formData.unit}
                onChange={(e) => setFormData((p) => ({ ...p, unit: e.target.value }))}
                className="w-full px-3 py-2 rounded-xl border border-[var(--color-border)] bg-white/[0.04] text-[var(--color-text)] text-sm focus:outline-none focus:border-primary-200/40"
              >
                <option value="kg" className="bg-[#0a1208]">kg</option>
                <option value="lbs" className="bg-[#0a1208]">lbs</option>
                <option value="%" className="bg-[#0a1208]">%</option>
                <option value="cm" className="bg-[#0a1208]">cm</option>
                <option value="inches" className="bg-[#0a1208]">inches</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.18em] text-[var(--color-text-muted)] mb-1.5">
              {t('progress.form.notes', { defaultValue: 'Notes' })}
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData((p) => ({ ...p, notes: e.target.value }))}
              rows={2}
              placeholder={t('progress.form.notesPlaceholder', { defaultValue: 'Any context…' })}
              className="w-full px-3 py-2 rounded-xl border border-[var(--color-border)] bg-white/[0.04] text-[var(--color-text)] text-sm focus:outline-none focus:border-primary-200/40 resize-none"
            />
          </div>
          {error && (
            <div className="px-3 py-2 rounded-xl border border-red-500/30 bg-red-500/10 text-red-200 text-sm">
              {error}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:bg-white/[0.04]"
            >
              {t('progress.form.cancel', { defaultValue: 'Cancel' })}
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 rounded-xl bg-gradient-to-r from-primary-500 to-primary-300 text-[#0a1208] text-sm font-semibold hover:from-primary-300 hover:to-primary-200 transition-all disabled:opacity-50"
              data-testid="save-metric-btn"
            >
              {saving ? t('progress.form.saving', { defaultValue: 'Saving…' }) : t('progress.form.save', { defaultValue: 'Save' })}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function PhotoForm({ onClose, onSave, t }) {
  const [photoUrl, setPhotoUrl] = useState('');
  const [photoType, setPhotoType] = useState('front');
  const [notes, setNotes] = useState('');
  const [takenAt, setTakenAt] = useState(() => {
    const d = new Date();
    return d.toISOString().slice(0, 10);  // YYYY-MM-DD
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!photoUrl) {
      setError(t('progress.photoForm.errorUrl', { defaultValue: 'Photo URL is required' }));
      return;
    }
    setSaving(true);
    setError('');
    try {
      await progressAPI.uploadPhoto({
        photo_url: photoUrl,
        photo_type: photoType,
        notes: notes || undefined,
        taken_at: new Date(takenAt).toISOString(),
      });
      onSave();
    } catch (err) {
      setError(err.response?.data?.detail || t('progress.photoForm.errorSave', { defaultValue: 'Failed to save photo' }));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="photo-form-modal">
      <div className="rounded-2xl border border-[var(--color-border)] bg-[#0d1512] w-full max-w-md overflow-hidden">
        <div className="px-6 py-4 border-b border-[var(--color-border)] flex items-center justify-between">
          <div>
            <span className="hero-kicker">{t('progress.photoForm.kicker', { defaultValue: 'New' })}</span>
            <h2 className="mt-1 font-display text-lg uppercase text-[var(--color-text)]">
              {t('progress.photoForm.title', { defaultValue: 'Add a progress photo' })}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-xs uppercase tracking-[0.18em] text-[var(--color-text-muted)] mb-1.5">
              {t('progress.photoForm.url', { defaultValue: 'Photo URL' })}
            </label>
            <input
              type="url"
              value={photoUrl}
              onChange={(e) => setPhotoUrl(e.target.value)}
              placeholder="https://..."
              className="w-full px-3 py-2 rounded-xl border border-[var(--color-border)] bg-white/[0.04] text-[var(--color-text)] text-sm focus:outline-none focus:border-primary-200/40"
              data-testid="photo-form-url"
            />
            <p className="mt-1 text-[10px] text-[var(--color-text-muted)]">
              {t('progress.photoForm.urlHint', { defaultValue: 'Hosted image URL — file upload coming soon.' })}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs uppercase tracking-[0.18em] text-[var(--color-text-muted)] mb-1.5">
                {t('progress.photoForm.type', { defaultValue: 'View' })}
              </label>
              <select
                value={photoType}
                onChange={(e) => setPhotoType(e.target.value)}
                className="w-full px-3 py-2 rounded-xl border border-[var(--color-border)] bg-white/[0.04] text-[var(--color-text)] text-sm focus:outline-none focus:border-primary-200/40"
              >
                <option value="front" className="bg-[#0a1208]">{t('progress.photoForm.front', { defaultValue: 'Front' })}</option>
                <option value="side" className="bg-[#0a1208]">{t('progress.photoForm.side', { defaultValue: 'Side' })}</option>
                <option value="back" className="bg-[#0a1208]">{t('progress.photoForm.back', { defaultValue: 'Back' })}</option>
              </select>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.18em] text-[var(--color-text-muted)] mb-1.5">
                {t('progress.photoForm.date', { defaultValue: 'Date' })}
              </label>
              <input
                type="date"
                value={takenAt}
                onChange={(e) => setTakenAt(e.target.value)}
                className="w-full px-3 py-2 rounded-xl border border-[var(--color-border)] bg-white/[0.04] text-[var(--color-text)] text-sm focus:outline-none focus:border-primary-200/40"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.18em] text-[var(--color-text-muted)] mb-1.5">
              {t('progress.photoForm.notes', { defaultValue: 'Notes' })}
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 rounded-xl border border-[var(--color-border)] bg-white/[0.04] text-[var(--color-text)] text-sm focus:outline-none focus:border-primary-200/40 resize-none"
            />
          </div>
          {error && (
            <div className="px-3 py-2 rounded-xl border border-red-500/30 bg-red-500/10 text-red-200 text-sm">
              {error}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:bg-white/[0.04]"
            >
              {t('progress.form.cancel', { defaultValue: 'Cancel' })}
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 rounded-xl bg-gradient-to-r from-primary-500 to-primary-300 text-[#0a1208] text-sm font-semibold hover:from-primary-300 hover:to-primary-200 transition-all disabled:opacity-50"
              data-testid="save-photo-btn"
            >
              {saving ? t('progress.form.saving', { defaultValue: 'Saving…' }) : t('progress.form.save', { defaultValue: 'Save' })}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ===========================================================================
// Helpers
// ===========================================================================

function colorClasses(color) {
  return {
    primary: 'border-[rgba(213,255,123,0.18)] bg-[rgba(213,255,123,0.05)] text-primary-100',
    blue: 'border-[rgba(95,180,255,0.18)] bg-[rgba(95,180,255,0.05)] text-blue-200',
    purple: 'border-[rgba(195,140,255,0.18)] bg-[rgba(195,140,255,0.05)] text-purple-200',
    amber: 'border-[rgba(255,210,125,0.18)] bg-[rgba(255,210,125,0.05)] text-amber-200',
  }[color] || '';
}

function cn(...args) {
  return args.filter(Boolean).join(' ');
}

function formatShortDate(d) {
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function formatFullDate(d) {
  return d.toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

export default ProgressTracking;
